import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Check, ArrowRight, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Claim() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    if (!token) { setState({ loading: false, valid: false }); return; }
    localStorage.setItem("pmw_claim", token);
    api.post("/claim/info", { token })
      .then(({ data }) => setState({ loading: false, ...data }))
      .catch(() => setState({ loading: false, valid: false }));
  }, [token]);

  const go = (mode) => {
    const q = new URLSearchParams();
    if (mode === "register") q.set("mode", "register");
    if (state.buyer_email) q.set("email", state.buyer_email);
    navigate(`/login?${q.toString()}`);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background grain p-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md text-center">
        <div className="flex items-center justify-center gap-2 mb-8"><Heart className="h-6 w-6 text-accent" fill="currentColor" /><span className="tracking-[0.2em] text-sm text-primary">PLANMYWEDDING</span></div>

        {state.loading ? (
          <p className="text-muted-foreground">Checking your purchase…</p>
        ) : state.valid ? (
          <div className="rounded-2xl bg-card border border-border p-8 shadow-[0_20px_50px_rgb(0,0,0,0.06)]">
            <div className="h-14 w-14 rounded-full bg-accent/15 flex items-center justify-center mx-auto mb-5"><Check className="h-6 w-6 text-accent" /></div>
            <h1 className="text-3xl tracking-tight text-primary">Your purchase is confirmed</h1>
            <p className="text-muted-foreground mt-3">Create your account to unlock <b>{state.product}</b>. Your planner will be linked automatically{state.buyer_email ? ` to ${state.buyer_email}` : ""}.</p>
            <button onClick={googleLogin} className="w-full flex items-center justify-center gap-3 border border-border rounded-xl py-3 hover:bg-secondary transition-colors mt-7" data-testid="claim-google">
              <img alt="" src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="h-5 w-5" />
              <span className="text-sm font-medium">Continue with Google</span>
            </button>
            <div className="flex items-center gap-3 my-4"><div className="h-px bg-border flex-1" /><span className="text-xs text-muted-foreground">or</span><div className="h-px bg-border flex-1" /></div>
            <Button onClick={() => go("register")} className="w-full rounded-full py-6" data-testid="claim-register">Create my account <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
            <button onClick={() => go("login")} className="text-sm text-accent hover:underline mt-5 block mx-auto" data-testid="claim-login">I already have an account</button>
          </div>
        ) : (
          <div className="rounded-2xl bg-card border border-border p-8">
            <div className="h-14 w-14 rounded-full bg-secondary flex items-center justify-center mx-auto mb-5"><AlertCircle className="h-6 w-6 text-muted-foreground" /></div>
            <h1 className="text-2xl tracking-tight text-primary">This link isn't valid</h1>
            <p className="text-muted-foreground mt-3">It may have expired or already been used. You can still log in, or contact support with your Etsy order number.</p>
            <Button onClick={() => navigate("/login")} className="rounded-full mt-6" data-testid="claim-fallback-login">Go to login</Button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
